/* Drawer owns invalid-gift cleanup; only the cart page adds gifts at checkout. */
(function (scope) {
  'use strict';
  const core = scope.KateCartGiftCore;
  const drawer = scope.document.getElementById('mini-cart');
  if (!core || !drawer) return;
  const root = scope.document.getElementById('zyf-gift-promotions-root');
  // Runs before the deferred app assets. Never take over an already-running writer.
  if (!root || root.dataset.initialized === 'true') {
    scope.console?.warn('Gift drawer adapter could not acquire ownership; reminder disabled.');
    return;
  }
  root.dataset.initialized = 'true';
  root.dataset.conflictInitialized = 'true';
  root.dataset.kateGiftOwner = 'drawer';
  const route = scope.Shopify?.routes?.root || '/';
  let generation = 0, controller, timer, updates = 0, pendingRender = '';
  const hide = () => drawer.querySelectorAll('[data-cart-gift-notice]').forEach(node => { node.hidden = true; });
  const signature = cart => JSON.stringify([cart.total_price, cart.currency, cart.items.map(line =>
    [line.key, line.variant_id, line.quantity, line.final_line_price, line.properties])]);
  const read = (document, id) => {
    const node = document.getElementById(id);
    if (!node) throw new Error('Missing gift projection');
    const value = JSON.parse(node.textContent);
    return typeof value === 'string' ? JSON.parse(value) : value;
  };
  async function check(version, signal) {
    const initialSafety = await scope.KateGiftSafety.ensureLocked({ checkout: true });
    if (initialSafety.bypassGiftFlow) return;
    const request = async (path, html = false, accept = html ? 'text/html' : 'application/json') => {
      const response = await scope.fetch(route + path, {
        credentials: 'same-origin', cache: 'no-store', signal,
        headers: { Accept: accept }
      });
      if (!response.ok) throw new Error('Gift check failed');
      return html ? response.text() : response.json();
    };
    const [html, initialCart] = await Promise.all([request('cart?zyf_gift_runtime=1', true), request('cart.js')]);
    let cart = initialCart;
    const projection = new scope.DOMParser().parseFromString(html, 'text/html');
    const config = read(projection, 'zyf-gift-promotions-config');
    const facts = read(projection, 'zyf-gift-promotions-cart-context').items;
    if (!Array.isArray(config.rules) || !Array.isArray(facts) || !Array.isArray(cart.items)) return;
    if (cart.items.some(line => !core.managed(line) && !facts.some(fact => String(fact.variantId) === String(line.variant_id)))) return;
    let allows = () => true;
    if (config.rules.some(rule => rule.allowedCountryCodes?.length)) {
      if (!scope.ZyfGiftCountry) return;
      let detected = '';
      // Force the app's country reader to report its result without writing attributes.
      const gate = scope.ZyfGiftCountry(config, async attributes => {
        detected = attributes._zyf_gift_ip_country || '';
        return cart;
      });
      await gate.sync({ ...cart, attributes: { ...cart.attributes, _zyf_gift_ip_country: '__read_only__' } });
      if (!detected) return;
      allows = gate.allows;
    }
    const candidates = new Map(config.rules.flatMap(rule => rule.tiers.flatMap(tier => tier.candidates))
      .map(candidate => [core.suffix(candidate.variantId), candidate]));
    const products = new Map(), stock = new Map(), choices = new Map();
    for (const candidate of candidates.values()) {
      if (!candidate.productHandle) throw new Error('Missing gift product');
      const list = products.get(candidate.productHandle) || [];
      list.push(candidate); products.set(candidate.productHandle, list);
    }
    await Promise.all([...products].map(async ([handle, items]) => {
      const product = await request(`products/${encodeURIComponent(handle)}?view=gift-stock`, false, 'text/html');
      if (!Array.isArray(product.variants)) throw new Error('Missing gift stock');
      for (const candidate of items) {
        const id = core.suffix(candidate.variantId);
        const variant = product.variants.find(item => String(item.id) === id);
        if (variant && typeof variant.giftAvailable !== 'boolean') throw new Error('Unknown gift stock');
        stock.set(id, variant?.giftAvailable === true);
      }
    }));
    try {
      const stored = JSON.parse(scope.sessionStorage.getItem('kate-cart-gift-choices-v1'));
      if (stored?.revision === config.revision) stored.values.forEach(([key, value]) => choices.set(key, value));
    } catch {}
    // Adopt a valid existing selection just as the cart page does on entry.
    for (const line of cart.items.filter(core.managed)) {
      const rule = config.rules.find(item => item.id === line.properties?.[core.ATTR.rule]);
      const tier = rule?.tiers.find(item => item.id === line.properties?.[core.ATTR.tier]);
      const candidate = tier?.candidates.find(item => core.suffix(item.variantId) === String(line.variant_id));
      if (candidate && line.quantity === 1 && Number(line.final_line_price ?? line.line_price) === 0 &&
          Object.entries(core.properties(config, { rule, tier, candidate })).every(([key, value]) => line.properties?.[key] === value)) {
        choices.set(`${rule.id}:${tier.id}`, String(line.variant_id));
      }
    }
    const rate = Number(scope.Shopify?.currency?.rate || 1) || 1;
    if (version !== generation || signal.aborted || updates) return;
    const safe = await scope.KateGiftSafety.ensureLocked({ checkout: true });
    cart = safe.cart;
    if (safe.removedKeys.length || safe.bypassGiftFlow) return;
    const reward = core.resolve(config, cart, facts, stock, choices, rate, undefined, allows);
    const states = cart.items.filter(core.managed).map(line =>
      core.giftState(config, cart, line, facts, stock, choices, rate, undefined, allows));
    const latest = await request('cart.js');
    if (signature(latest) !== signature(cart)) { schedule(); return; }
    if (version !== generation || signal.aborted || !drawer.hasAttribute('open')) return;
    const renderedNode = drawer.querySelector('[data-cart-gift-snapshot]');
    if (!renderedNode) return;
    const rendered = JSON.parse(renderedNode.textContent);
    if (signature(rendered) !== signature(latest)) {
      if (pendingRender !== signature(latest)) {
        pendingRender = signature(latest);
        scope.document.dispatchEvent(new scope.CustomEvent('cart:refresh', { detail: { cart: latest } }));
      }
      return;
    }
    pendingRender = '';
    if (!reward || states.some(state => state === 'valid' || state === 'unknown')) return;
    drawer.querySelectorAll('[data-cart-gift-notice]').forEach(node => { node.hidden = false; });
  }
  function schedule() {
    generation += 1;
    const version = generation;
    controller?.abort(); clearTimeout(timer); hide();
    if (!drawer.hasAttribute('open') || updates) return;
    timer = scope.setTimeout(() => {
      controller = new scope.AbortController();
      const signal = controller.signal;
      const timeout = scope.setTimeout(() => controller?.signal === signal && controller.abort(), 12000);
      void scope.KateGiftSafety.exclusive(() => check(version, signal)).catch(error => {
        if (signal.aborted) return;
        scope.console?.warn('Gift drawer check failed:', error.message);
      }).finally(() => clearTimeout(timeout));
    }, 200);
  }
  const observer = new scope.MutationObserver(records => {
    if (records.some(record => record.type === 'childList' || record.attributeName === 'open')) schedule();
  });
  // CartDrawer replaces its own children; app widgets mutate nested text constantly.
  observer.observe(drawer, { childList: true, attributes: true, attributeFilter: ['open'] });
  ['cart:updated', 'cart:refresh', 'variant:added', 'ajaxProduct:added', 'product:added'].forEach(name =>
    scope.document.addEventListener(name, schedule));
  scope.document.addEventListener('line-item-quantity:change:start', () => { updates += 1; schedule(); });
  scope.document.addEventListener('line-item-quantity:change:end', () => { updates = Math.max(0, updates - 1); schedule(); });
  scope.document.addEventListener('zyf:gift-selection-request', event => {
    scope.document.dispatchEvent(new scope.CustomEvent('zyf:gift-selection-result', {
      detail: { ...event.detail, ok: false, message: 'Please choose your gift on the cart page.' }
    }));
  });
  drawer.addEventListener('change', event => {
    if (event.target.matches('.quantity-selector__input')) schedule();
  });
  drawer.addEventListener('click', event => {
    if (event.target.closest('line-item-quantity')) schedule();
  });
  scope.addEventListener('pageshow', schedule);
  scope.document.addEventListener('visibilitychange', () => { if (!scope.document.hidden) schedule(); });
  schedule();
})(window);
